import { ProductItem } from "~/contract/vo/ProductRelated.vo";
import { ReviewItem } from "~/contract/vo/ReviewRelated.vo";
import ApiService from "~/service/ApiService";
import { useAuthStore } from "./auth";
import { ProductVariantOptionItem } from "~/contract/vo/ProductVariantRelated.vo";

export const useProductCustomStore = defineStore("productCustom", {
  state: () => ({
    product: null as unknown as ProductItem,
    recommendedProduct: [] as Array<ProductItem>,
    upsellProducts: [] as Array<ProductItem>, // [NEW] State for upsell products
    selectedUpsellItems: [] as Array<string>, // [NEW] State for selected upsell item IDs
    upsellSelectedVariants: {} as Record<string, ProductVariantOptionItem>, // [NEW] State for selected variants of upsell products
    latestProduct: [] as Array<ProductItem>,
    reviews: [] as Array<ReviewItem>,
    selectedVariantOption: null as unknown as ProductVariantOptionItem,
    productLoading: false,
    recommendedProductLoading: false,
    upsellProductsLoading: false, // [NEW] Loading state
    latestProductLoading: false,
  }),
  actions: {
    async retrieveProduct(urlSlug: string) {
      this.productLoading = true;
      const { data, pending, error }: any = useAuthStore().authenticated
        ? await ApiService.get("/api/v1/product", urlSlug, false)
        : await ApiService.get("/api/v1/product/public", urlSlug, false);
      this.productLoading = pending.value;
      if (error.value) {
        this.productLoading = true;
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as ProductItem;
        this.product = response;
      }
    },
    async retrieveRecommendedProduct(id: string) {
      this.recommendedProductLoading = true;
      const { data, pending, error }: any = useAuthStore().authenticated
        ? await ApiService.get("/api/v1/product/recommend", id, true)
        : await ApiService.get("/api/v1/product/public/recommend", id, true);
      this.recommendedProductLoading = pending.value;
      if (error.value) {
        this.recommendedProductLoading = true;
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as Array<ProductItem>;
        this.recommendedProduct = response;
      }
    },
    // [NEW] Action to retrieve Upsell Products
    async retrieveUpsellProducts(id: string) {
      this.upsellProductsLoading = true;
      // Reset selection when fetching new upsell products logic handled in component usually, but good to clear here or in component
      this.selectedUpsellItems = [];
      this.upsellSelectedVariants = {};

      const { data, pending, error }: any = useAuthStore().authenticated
        ? await ApiService.get("/api/v1/product/upsell", id, true)
        : await ApiService.get("/api/v1/product/public/upsell", id, true);

      this.upsellProductsLoading = pending.value;
      if (error.value) {
        this.upsellProductsLoading = false;
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as Array<ProductItem>;
        this.upsellProducts = response;

        // Initialize default variants (1st option)
        this.upsellProducts.forEach(p => {
          if (p.variant && p.variant.variantOption && p.variant.variantOption.length > 0) {
            this.upsellSelectedVariants[p.id] = p.variant.variantOption[0];
          }
        });

        // [NEW] Auto-check the first item if exists
        if (this.upsellProducts.length > 0) {
          // Prevent duplicates if multiple components call this
          this.selectedUpsellItems = [this.upsellProducts[0].id];
        } else {
          this.selectedUpsellItems = [];
        }
      }
    },
    // [NEW] Action to set selected variant for an upsell product
    setUpsellVariant(productId: string, variantOption: ProductVariantOptionItem) {
      this.upsellSelectedVariants[productId] = variantOption;
    },
    // [NEW] Action to toggle upsell item selection
    toggleUpsellItem(productId: string) {
      if (this.selectedUpsellItems.includes(productId)) {
        this.selectedUpsellItems = this.selectedUpsellItems.filter(id => id !== productId);
      } else {
        this.selectedUpsellItems.push(productId);
      }
    },
    async retrieveLatestProduct() {
      this.latestProductLoading = true;
      const { data, pending, error }: any = useAuthStore().authenticated
        ? await ApiService.query("/api/v1/product/latest", {}, true)
        : await ApiService.query("/api/v1/product/public/latest", {}, true);
      this.latestProductLoading = pending.value;
      if (error.value) {
        this.latestProductLoading = true;
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as Array<ProductItem>;
        this.latestProduct = response;
      }
    },
    async retrieveProductReviews(productId: string) {
      this.reviews = []; // Clear old reviews
      try {
        const endpoint = useAuthStore().authenticated
          ? `/api/v1/product-reviews/product`
          : `/api/v1/product-reviews/public/product`;

        const { data, pending, error }: any = await ApiService.get(
          endpoint,
          productId,
          false
        );

        if (error.value) {
          console.error('Error fetching reviews:', error.value);
          return;
        }

        if (data.value && data.value.data) {
          // Map backend response to VO
          this.reviews = data.value.data.map((r: any) => ({
            id: r.id,
            reviewerName: r.user ? r.user.firstName + ' ' + r.user.lastName : 'Anonymous',
            rating: r.rating,
            comment: r.comment,
            createdAt: r.createdAt,
            photos: r.photos ? r.photos.map((p: any) => p.photoUrl) : []
          }));
        }
      } catch (err) {
        console.error('Catch error in retrieveProductReviews:', err);
      }
    },

    async submitProductReview(productId: string, orderId: string, data: { rating: number; comment: string; reviewerName: string; photos?: any[] }) {
      let payload: any = {
        productId,
        orderId,
        rating: data.rating,
        comment: data.comment,
      };

      // Check if photos are actual Files (for multipart upload)
      if (data.photos && data.photos.length > 0 && data.photos[0] instanceof File) {
        const formData = new FormData();
        formData.append('productId', productId);
        formData.append('orderId', orderId);
        formData.append('rating', data.rating.toString());
        formData.append('comment', data.comment);

        data.photos.forEach((file) => {
          formData.append('photos', file);
        });

        payload = formData;
      } else {
        payload.photos = data.photos;
      }

      const result: any = await ApiService.post(
        "/api/v1/product-reviews",
        payload,
        true
      );

      if (result.error.value) {
        throw result.error.value;
      }

      // Refresh reviews after submission
      await this.retrieveProductReviews(productId);
    },
  },
});
